// Console logger that stores logs in memory for export
// Stores last 3MB of console logs

let isInitialized = false;
const originalConsoleLog = console.log;
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

// Storage for logs (circular buffer)
const MAX_STORAGE_BYTES = 3 * 1024 * 1024; // 3MB
let logBuffer = [];
let currentSize = 0;

// Store log entry in memory
function addLogToBuffer(level, args, timestamp) {
  const logEntry = {
    timestamp,
    level,
    message: args[0] ? String(args[0]) : '',
    data: args.length > 1 ? args.slice(1) : undefined,
    url: window.location.href
  };

  try {
    // Calculate size of this log entry
    const logString = JSON.stringify(logEntry);
    const logSize = new Blob([logString]).size;

    // If single log is larger than max size, truncate the data
    if (logSize > MAX_STORAGE_BYTES) {
      logEntry.data = { truncated: true, reason: 'Log entry too large', originalSize: logSize };
      const truncatedString = JSON.stringify(logEntry);
      const truncatedSize = new Blob([truncatedString]).size;
      
      // Remove old entries if needed
      while (currentSize + truncatedSize > MAX_STORAGE_BYTES && logBuffer.length > 0) {
        const removed = logBuffer.shift();
        const removedString = JSON.stringify(removed);
        const removedSize = new Blob([removedString]).size;
        currentSize -= removedSize;
      }
      
      logBuffer.push(logEntry);
      currentSize += truncatedSize;
      return;
    }

    // Remove old entries if buffer would be full
    while (currentSize + logSize > MAX_STORAGE_BYTES && logBuffer.length > 0) {
      const removed = logBuffer.shift();
      const removedString = JSON.stringify(removed);
      const removedSize = new Blob([removedString]).size;
      currentSize -= removedSize;
    }

    logBuffer.push(logEntry);
    currentSize += logSize;
  } catch (error) {
    // If we can't stringify, store a simplified version
    originalConsoleError('Error storing log:', error);
    const simpleEntry = {
      timestamp,
      level,
      message: String(args[0] || ''),
      error: 'Failed to serialize log data'
    };
    const simpleString = JSON.stringify(simpleEntry);
    const simpleSize = new Blob([simpleString]).size;
    
    while (currentSize + simpleSize > MAX_STORAGE_BYTES && logBuffer.length > 0) {
      const removed = logBuffer.shift();
      const removedString = JSON.stringify(removed);
      const removedSize = new Blob([removedString]).size;
      currentSize -= removedSize;
    }
    
    logBuffer.push(simpleEntry);
    currentSize += simpleSize;
  }
}

// Export logs as JSON file
export function exportLogs(comment = '', filename = `protokol-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.json`) {
  try {
    // Filter out warn logs
    const filteredLogs = logBuffer.filter(log => log.level !== 'warn');
    
    const logsData = {
      exported: new Date().toISOString(),
      totalLogs: filteredLogs.length,
      sizeBytes: currentSize,
      userAgent: navigator.userAgent,
      url: window.location.href,
      logs: filteredLogs
    };

    // Add comment if provided
    if (comment && comment.trim()) {
      logsData.comment = comment.trim();
    }

    const dataStr = JSON.stringify(logsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    return true;
  } catch (error) {
    originalConsoleError('Failed to export logs:', error);
    return false;
  }
}

// Get current logs (without downloading)
export function getLogs() {
  return {
    totalLogs: logBuffer.length,
    sizeBytes: currentSize,
    logs: [...logBuffer]
  };
}

// Clear logs from memory
export function clearLogs() {
  logBuffer = [];
  currentSize = 0;
}

// Get logs statistics
export function getLogsStats() {
  return {
    totalLogs: logBuffer.length,
    sizeBytes: currentSize,
    sizeMB: (currentSize / (1024 * 1024)).toFixed(2),
    sizeKB: (currentSize / 1024).toFixed(2),
    oldestLog: logBuffer.length > 0 ? logBuffer[0].timestamp : null,
    newestLog: logBuffer.length > 0 ? logBuffer[logBuffer.length - 1].timestamp : null
  };
}

// Show export modal using Bootstrap modal via Vue event bus
async function showExportModal(includeScreenshot = false) {
  return new Promise((resolve) => {
    const stats = getLogsStats();
    const logsSize = stats.sizeKB > 1024 ? `${stats.sizeMB} MB` : `${stats.sizeKB} KB`;
    
    // Emit event to show modal
    if (window.vm) {
      window.vm.$root.$emit('show-log-export-modal', {
        includeScreenshot,
        logsCount: stats.totalLogs,
        logsSize,
        callback: (confirmed, comment) => {
          resolve({ confirmed, comment });
        }
      });
    } else {
      // Fallback if Vue instance not available
      resolve({ confirmed: false, comment: '' });
    }
  });
}

// Override console methods
export function initConsoleLogger() {
  if (isInitialized) {
    return;
  }

  console.log = function(...args) {
    const timestamp = new Date().toISOString();
    originalConsoleLog.apply(console, args);
    addLogToBuffer('info', args, timestamp);
  };

  console.error = function(...args) {
    const timestamp = new Date().toISOString();
    originalConsoleError.apply(console, args);
    addLogToBuffer('error', args, timestamp);
  };

  console.warn = function(...args) {
    const timestamp = new Date().toISOString();
    originalConsoleWarn.apply(console, args);
    addLogToBuffer('warn', args, timestamp);
  };

  isInitialized = true;
}

// Restore original console methods
export function restoreConsole() {
  console.log = originalConsoleLog;
  console.error = originalConsoleError;
  console.warn = originalConsoleWarn;
  isInitialized = false;
}

// Setup keyboard shortcuts
export function setupLogExportShortcuts() {
  // Keyboard shortcuts for desktop
  document.addEventListener('keydown', async (event) => {
    // Ctrl+L or Cmd+L - Export logs only
    if ((event.ctrlKey || event.metaKey) && event.key === 'l' && !event.shiftKey) {
      event.preventDefault();
      
      const result = await showExportModal(false);
      if (result.confirmed) {
        const success = exportLogs(result.comment);
        if (success) {
          originalConsoleLog('Logs exported successfully');
        }
      }
    }
    
    // Ctrl+Shift+L or Cmd+Shift+L - Export logs with screenshot embedded
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'L') {
      event.preventDefault();
      
      const result = await showExportModal(true);
      if (!result.confirmed) return;
      
      try {
        // Take screenshot and get base64
        const { captureScreenshot } = await import('./screenshot');
        const screenshotBase64 = await captureScreenshot();
        
        // Filter out warn logs
        const filteredLogs = logBuffer.filter(log => log.level !== 'warn');
        
        // Export logs with screenshot embedded
        const logsData = {
          exported: new Date().toISOString(),
          totalLogs: filteredLogs.length,
          sizeBytes: currentSize,
          userAgent: navigator.userAgent,
          url: window.location.href,
          screenshot: screenshotBase64,
          logs: filteredLogs
        };

        // Add comment if provided
        if (result.comment && result.comment.trim()) {
          logsData.comment = result.comment.trim();
        }

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const dataStr = JSON.stringify(logsData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        
        const link = document.createElement('a');
        link.href = URL.createObjectURL(dataBlob);
        link.download = `protokol-logs-screenshot-${timestamp}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        
        originalConsoleLog('Logs with screenshot exported successfully');
      } catch (error) {
        originalConsoleError('Failed to export logs with screenshot:', error);
      }
    }
  });

  // Mobile tap gesture - 5 taps within 2 seconds
  let tapCount = 0;
  let tapTimer = null;
  let touchStartPos = null;
  const TAP_MOVE_THRESHOLD = 10; // pixels — ignore touches that moved more than this (scrolls)

  document.addEventListener('touchstart', (event) => {
    if (event.touches.length === 1) {
      touchStartPos = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    } else {
      touchStartPos = null;
    }
  }, { passive: true });

  document.addEventListener('touchend', async (event) => {
    // Only count single-finger taps
    if (event.changedTouches.length !== 1) return;

    // Ignore if the finger moved (scroll/swipe gesture)
    if (!touchStartPos) return;
    const dx = event.changedTouches[0].clientX - touchStartPos.x;
    const dy = event.changedTouches[0].clientY - touchStartPos.y;
    if (Math.abs(dx) > TAP_MOVE_THRESHOLD || Math.abs(dy) > TAP_MOVE_THRESHOLD) return;

    tapCount++;

    // Start the 2-second window on the first tap only
    if (tapCount === 1) {
      tapTimer = setTimeout(() => {
        tapCount = 0;
        tapTimer = null;
      }, 2000);
    }
    
    // If 5 taps reached within the window, show modal and export logs with screenshot
    if (tapCount === 5) {
      if (tapTimer) {
        clearTimeout(tapTimer);
        tapTimer = null;
      }
      tapCount = 0;
      
      const result = await showExportModal(true);
      if (!result.confirmed) return;
      
      try {
        // Take screenshot and get base64
        const { captureScreenshot } = await import('./screenshot');
        const screenshotBase64 = await captureScreenshot();
        
        // Filter out warn logs
        const filteredLogs = logBuffer.filter(log => log.level !== 'warn');
        
        // Export logs with screenshot embedded
        const logsData = {
          exported: new Date().toISOString(),
          totalLogs: filteredLogs.length,
          sizeBytes: currentSize,
          userAgent: navigator.userAgent,
          url: window.location.href,
          screenshot: screenshotBase64,
          logs: filteredLogs
        };

        // Add comment if provided
        if (result.comment && result.comment.trim()) {
          logsData.comment = result.comment.trim();
        }

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const dataStr = JSON.stringify(logsData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        
        const link = document.createElement('a');
        link.href = URL.createObjectURL(dataBlob);
        link.download = `protokol-logs-screenshot-${timestamp}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        
        originalConsoleLog('Logs with screenshot exported (mobile gesture)');
      } catch (error) {
        originalConsoleError('Failed to export logs with screenshot:', error);
      }
    }
  });
}
